<script>
    import { url, params, redirect, goto } from "@roxi/routify";
    import { AppStore, Panels } from "$/stores/AppStore";
    import {
        ClusterAccessLevel,
        ClusterStore,
        ClusterType,
    } from "$/stores/ClusterStore";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { TokenStore } from "$/stores/TokenStore";
    import { TenantStore } from "$/stores/TenantStore";
    import ClusterLeaveModal from "./modals/ClusterLeaveModal.svelte";
    import {getRole, getEffectivePricingPlan, canUserEditTeamCluster} from "$/utils/Utils";
    import { tooltip } from "$/utils/tooltip";
    import { clickAction } from "$/utils/clickAction";
    import InteractiveHoverIcon from "./ui/InteractiveHoverIcon.svelte";
    import { onMount } from "svelte";
    import { SidebarStore } from "$/stores//SidebarStore";
    import Sortable from "sortablejs";

    export let cluster;
    export let addNewClicked;
    let showCopilotAnimation = false;

    let isDraggedOverTopic = null;
    let showLeaveModal = false;

    let clusterIcons = {
        [ClusterType.SHARED]: "multiple-users",
        [ClusterType.SHARED_WITH_ME]: "share",
        [ClusterType.ARCHIVED]: "archive",
        [ClusterType.TENANT]: "squares-2x2",
        [ClusterType.RESTRICTED]: "lock",
        [ClusterType.TENANT_DEFAULT]: TenantStore.isPersonal()
            ? "user"
            : "building",
    };
    let hoverdIconRef;
    let sortableTopicsWrapper;
    let clusterHoveredTimeoutId = null;

    const dragAndDropConfig = {
        group: "topics",
        handle: ".sidebar-list__item-wrapper",
        animation: 150,
        swapThreshold: 0.1,
        delay: 200,
        delayOnTouchOnly: true,
        onStart(event) {
            // Add quick vibration feedback when drag starts
            if (navigator.vibrate) {
                navigator.vibrate(50);
            }

            // Add a clone in the original location
            const persistentClone = event.from.insertBefore(
                event.item.cloneNode(true),
                event.from.children[event.oldIndex + 1],
            );

            // give it a class so we can remove it later
            persistentClone.classList.add("sortable-clone");

            // remove irrelevant classes
            persistentClone.classList.remove(
                "sortable-chosen",
                "sortable-ghost",
            );
        },
        async onEnd(event) {
            event.from
                .querySelectorAll(".sortable-clone")
                .forEach((child) => child.remove());

            const toClusterId = event.to.getAttribute("data-clusterId");
            const toClusterType = event.to.getAttribute("data-clusterType");
            const fromClusterId = event.from.getAttribute("data-clusterId");
            const fromClusterType = event.from.getAttribute("data-clusterType");

            if (
                fromClusterId === toClusterId &&
                fromClusterType === toClusterType
            ) {
                await ClusterStore.changeTopicPositionInCluster(
                    toClusterId,
                    toClusterType,
                    event.oldIndex,
                    event.newIndex,
                );
            } else {
                let clusterTo = ClusterStore.getClusterByIdAndType(
                    toClusterId,
                    toClusterType,
                );
                let clusterFrom = ClusterStore.getClusterByIdAndType(
                    fromClusterId,
                    fromClusterType,
                );
                let topic = clusterFrom.topics[event.oldIndex];
                await changeTopicCluster(topic, clusterTo, event.newIndex);
            }
        },
    };

    $: effectivePricingPlan = getEffectivePricingPlan($TenantStore?.active);
    $: canEditCluster = cluster?.clusterRoles?.find(
        (el) =>
            el.user.id == $TokenStore.user.id &&
            el.role === TopicAccessLevel.OWNER || el.role === TopicAccessLevel.EDITOR,
    );
    $: isClusterOwner = cluster?.clusterRoles?.find(
        (el) =>
            el.user.id == $TokenStore.user.id &&
            el.role === TopicAccessLevel.OWNER,
    );
    $: isRestrictedCluster =
        cluster?.accessLevel === ClusterAccessLevel.RESTRICTED;
    $: hasOwnerAccess =
        (isRestrictedCluster && canEditCluster) || !isRestrictedCluster;
    $: clusterIsEditable =
        !cluster?.loading &&
        cluster?.clusterType === ClusterType.TENANT &&
        hasOwnerAccess;
    $: canAddTopicsToCluster =
        effectivePricingPlan !== -1 &&
        (clusterIsEditable ||
            cluster?.clusterType === ClusterType.TENANT_DEFAULT ||
            cluster?.clusterType === ClusterType.ARCHIVED);
    $: canRemoveTopicsFromCluster =
        effectivePricingPlan !== -1 &&
        (clusterIsEditable ||
            cluster?.clusterType === ClusterType.TENANT_DEFAULT ||
            cluster?.clusterType === ClusterType.ARCHIVED);

    $: topics = cluster?.topics?.sort((a, b) => a.weight - b.weight);

    $: userTopicRole =
        $ActiveTopic &&
        getRole(
            $ActiveTopic,
            $TokenStore.user.id,
            $TenantStore?.tenants,
            $ClusterStore.clusters,
        );
    $: collapsed = $ClusterStore ? ClusterStore.isCollapsed(cluster) : false;

    //whenever collapsed is changed rotate the carret icon
    $: collapsed, rotateCarretIcon();

    $: {
        if ($TenantStore) {
            showCopilotAnimation =
                $TenantStore.activeAuthorities?.find(
                    (a) => a.authority === "ROLE_COPILOT_BETA",
                ) || effectivePricingPlan === 2;
        }
    }

    $: {
        if (sortableTopicsWrapper && canRemoveTopicsFromCluster) {
            Sortable.create(sortableTopicsWrapper, dragAndDropConfig);
        }
    }

    function handleTitleClick() {
        // return if not editable
        if (!clusterIsEditable) return;

        // if same cluster toggle panel, otherwise open new cluster
        if ($ClusterStore.active?.id === cluster.id) {
            AppStore.togglePanel(Panels.CLUSTER);
        } else {
            $ClusterStore.active = cluster;
            AppStore.setPanel(Panels.CLUSTER);
        }
    }

    async function handleTopicsClusterChange(topic, toCluster) {
        let updatedTopicFields;

        //Topic moved to archive
        if (!topic.archived && toCluster.clusterType === ClusterType.ARCHIVED) {
            updatedTopicFields = getArchiveUpdateField(topic);
        } else {
            //Topic moved out of archive
            if (
                topic.archived &&
                toCluster.clusterType !== ClusterType.ARCHIVED
            ) {
                updatedTopicFields = getArchiveUpdateField(topic);
            }
            //Topic moved to different cluster
            if (
                topic.clusterId !== toCluster.id ||
                (!topic.clusterId &&
                    topic.cluster.clusterType !== toCluster.clusterType)
            ) {
                updatedTopicFields = {
                    ...updatedTopicFields,
                    ...getUpdateClusterFields(topic, toCluster),
                };
            }
        }

        ClusterStore.changeTopicCluster(topic, toCluster);
        ClusterStore.updateTopic(updatedTopicFields);

        let activeTopicStoreBeforeUpdate = $ActiveTopic;
        ActiveTopicStore.updateTopic(updatedTopicFields);

        if (activeTopicStoreBeforeUpdate?.id === topic.id) {
            ActiveTopicStore.reloadCollaboratorsAfterClusterChange(toCluster);
        }
    }

    function getArchiveUpdateField(topic) {
        return {
            id: topic.id,
            weight: topic.weight,
            archived: !topic.archived,
        };
    }

    function getUpdateClusterFields(topic, cluster) {
        return {
            id: topic.id,
            weight: topic.weight,
            clusterId: cluster.id,
            cluster: {
                id: cluster.id,
                clusterType: cluster.clusterType,
            },
        };
    }

    function dragOverTopic(event, topicId) {
        event.preventDefault();
        isDraggedOverTopic = topicId;
    }

    function dragLeaveTopic(event, topicId) {
        event.preventDefault();
        isDraggedOverTopic = null;
    }

    function dropOnTopic(event, topicId) {
        event.preventDefault();

        isDraggedOverTopic = null;
        $AppStore.snippetIsDragging = false;

        let snippet = {
            id: $ActiveTopicStore.draggingSnippetId,
            topicId: topicId,
        };

        ActiveTopicStore.updateSnippet(snippet);
    }

    function userCanEditTopic(topic) {
        // Allow editing for inbox snippets (or snippets without topics)
        if (!topic || !topic.roles) {
            return true;
        }

        // otherwise check normally
        if (topic.roles && !topic.roles.length) {
            return !!cluster.clusterRoles.find(
                (el) =>
                    el.user.id == $TokenStore.user.id &&
                    el.role > TopicAccessLevel.COMMENTER,
            );
        } else {
            return (
                getRole(
                    topic,
                    $TokenStore.user.id,
                    $TenantStore?.tenants,
                    $ClusterStore.clusters,
                ) > 2
            );
        }
    }

    async function leaveCluster(cluster) {
        await ClusterStore.leaveCluster(cluster);
        const sharedTopics = cluster.topics.filter(
            (topic) =>
                topic.roles &&
                topic.roles.find(
                    (role) =>
                        role.user.id === $TokenStore?.user?.id &&
                        role.role > TopicAccessLevel.NO_ACCESS,
                ),
        );
        if (sharedTopics.length && $TenantStore?.active?.personal) {
            sharedTopics.forEach((sharedTopic) =>
                ClusterStore.addTopicToCluster(
                    sharedTopic,
                    ClusterType.SHARED_WITH_ME,
                ),
            );
        }

        $redirect("/");
    }

    function collapseCluster() {
        ClusterStore.toggleCollapsed(cluster);
    }

    function rotateCarretIcon() {
        if (hoverdIconRef)
            hoverdIconRef.style.transform = collapsed
                ? "rotate(0deg)"
                : "rotate(90deg)";
    }

    function onTopicClick(topicId) {
        SidebarStore.highlightTopic(topicId);
    }

    function collapseClusterOnTopicDrag() {
        if (collapsed && !clusterHoveredTimeoutId) {
            clusterHoveredTimeoutId = setTimeout(() => {
                collapseCluster();
                clearTopicDraggingTimeout();
            }, 800);
        }
    }

    function clearTopicDraggingTimeout() {
        clearTimeout(clusterHoveredTimeoutId);
        clusterHoveredTimeoutId = null;
    }

    async function handleTopicDroppedOnCluster(event) {
        if (collapsed && clusterHoveredTimeoutId) {
            const topicJson = event.dataTransfer.getData("application/json");
            if (topicJson) {
                const topic = JSON.parse(topicJson);

                let destinationCluster = ClusterStore.getClusterByIdAndType(
                    topic.cluster.id,
                    topic.cluster.clusterType,
                );

                let topicCanMove = false;

                if (TenantStore.isPersonal()) {
                    topicCanMove =
                        ownsCluster(cluster) &&
                        ownsCluster(destinationCluster) &&
                        ownsTopic(topic);
                } else {
                    topicCanMove =
                        canUserEditTeamCluster(cluster) &&
                        canUserEditTeamCluster(destinationCluster);
                }

                if (topicCanMove)
                    await changeTopicCluster(
                        topic,
                        cluster,
                        cluster.topics.length,
                    );
            }
            clearTopicDraggingTimeout();
        }
    }

    function ownsTopic(topic) {
        const userId = $TokenStore?.user?.id;
        return topic.createdById === userId || topic.createdBy?.id === userId;
    }

    function ownsCluster(cluster) {
        const userId = $TokenStore?.user?.id;
        return (
            cluster.clusterType === ClusterType.TENANT_DEFAULT ||
            cluster.clusterType === ClusterType.ARCHIVED ||
            cluster?.clusterRoles?.find(
                (el) =>
                    el.user.id === userId && el.role === TopicAccessLevel.OWNER,
            )
        );
    }

    async function changeTopicCluster(topic, cluster, positionIndex) {
        let weight = ClusterStore.getTopicsWeightInClusterAtPositionIndex(
            cluster,
            positionIndex,
        );
        ClusterStore.updateClusterTopicsWithSameWeight(
            {
                ...topic,
                cluster: cluster,
                clusterId: cluster.id,
            },
            weight,
        );
        await handleTopicsClusterChange({ ...topic, weight: weight }, cluster);
    }

    function isActiveTopicInArchivedCluster() {
        return (
            cluster.clusterType === ClusterType.ARCHIVED &&
            $ActiveTopic?.archived
        );
    }

    function isActiveTopicInCollapsedCluster() {
        return (
            cluster.collapsed &&
            !$ActiveTopic?.archived &&
            cluster.topics?.some((topic) => topic.id === $ActiveTopic?.id)
        );
    }

    onMount(() => {
        if (
            isActiveTopicInArchivedCluster() ||
            isActiveTopicInCollapsedCluster()
        ) {
            ClusterStore.toggleCollapsed(cluster);
        }

        // cluster's caret default position is horizontal, on mount if the current active topic is archived we
        // rotate all carets, if not we rotate all caret icons except the archive cluster
        if (!collapsed) hoverdIconRef.style.transform = "rotate(90deg)";
    });
</script>

<ClusterLeaveModal
    bind:showModal={showLeaveModal}
    confirm={() => leaveCluster(cluster)}
/>

<!--Dragover preventDefault is a hack because for some reason on:drop would not register on Archive and My topics clusters.
 Source: https://stackoverflow.com/questions/32084053/why-is-ondrop-not-working -->
<div
    class={`${showCopilotAnimation ? "sidebar-list-title sidebar-list-title-copilot-access" : "sidebar-list-title"}`}
    class:dragged-topic-over-cluster={clusterHoveredTimeoutId !== null}
    on:dragover={collapseClusterOnTopicDrag}
    on:dragleave={clearTopicDraggingTimeout}
    on:dragover={(event) => event.preventDefault()}
    on:drop={handleTopicDroppedOnCluster}
>
    <div class="sidebar-list-title-content group">
        <InteractiveHoverIcon
            iconName={clusterIcons[cluster?.clusterType]}
            hoverIconName="carret"
            action={collapseCluster}
            bind:hoverdIconRef
        />

        <div class="sidebar-list-title__name" use:clickAction={collapseCluster}>
            {cluster?.name}
        </div>

        {#if addNewClicked !== null && canAddTopicsToCluster && cluster.clusterType !== ClusterType.ARCHIVED}
            {#if cluster.clusterType !== ClusterType.TENANT_DEFAULT && isClusterOwner}
                <div
                    class="sidebar-list-title__button"
                    use:tooltip={"Settings"}
                    use:clickAction={handleTitleClick}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
                        />
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                    </svg>
                </div>
            {/if}
            <div
                class="sidebar-list-title__button"
                use:tooltip={"Add New Topic"}
                use:clickAction={() => {
                    AppStore.closePanels();
                    addNewClicked();
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="1.5"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 4v16m8-8H4"
                    />
                </svg>
            </div>
        {:else if cluster.clusterType === ClusterType.SHARED}
            <div
                class="sidebar-list-title__button"
                use:tooltip={"Leave cluster"}
                use:clickAction={() => (showLeaveModal = true)}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M8.25 9.75h4.875a2.625 2.625 0 010 5.25H12M8.25 9.75L10.5 7.5M8.25 9.75L10.5 12m9-7.243V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0c1.1.128 1.907 1.077 1.907 2.185z"
                    />
                </svg>
            </div>
        {/if}
    </div>
</div>

<div
    bind:this={sortableTopicsWrapper}
    class="sidebar-list"
    data-clusterType={cluster.clusterType}
    data-clusterId={cluster.id}
    class:menu-hidden={collapsed}
    class:sidebar-list--hidden={$AppStore.sidebar.itemIsDragging === true}
>
    {#if topics === null}
        <a class="sidebar-list__item-wrapper" href={"#"}>
            <li class="sidebar-list__item sidebar-list__item--loading">
                Loading topics...
            </li>
        </a>
    {:else if topics.length === 0}
        <a class="sidebar-list__item-wrapper" href={"#"}>
            <li class="sidebar-list__item sidebar-list__item--loading">
                No topics yet
            </li>
        </a>
    {:else}
        {#each topics as topic, i (topic)}
            <div
                class="sidebar-list__item-wrapper"
                on:dragstart={(event) => {
                    event.dataTransfer.setData(
                        "application/json",
                        JSON.stringify(topic),
                    );
                }}
            >
                {#if userTopicRole > TopicAccessLevel.COMMENTER && userCanEditTopic(topic)}
                    <div
                        class="sidebar-list__item-drop-area"
                        class:sidebar-list__item-drop-area--hidden={$AppStore.snippetIsDragging !==
                            true}
                        on:dragover={(event) => dragOverTopic(event, topic.id)}
                        on:dragleave={(event) =>
                            dragLeaveTopic(event, topic.id)}
                        on:drop={(event) => dropOnTopic(event, topic.id)}
                    ></div>
                {/if}

                <a
                    href={$url(`/${topic.id}`)}
                    id={`topic-${topic.id}`}
                    class="sidebar-list__item"
                    class:sidebar-list__item--dragover={isDraggedOverTopic ===
                        topic.id}
                >
                    <svg
                        class="sidebar-list__item-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                        />
                    </svg>
                    <span
                        class="sidebar-list__item-name"
                        class:sidebar-list__item-name--active={$params.topicId ===
                            topic.id}
                    >
                        {topic.name}
                    </span>
                    <div
                        class="sidebar-list__item-drop-image"
                        class:sidebar-list__item-drop-image--hidden={isDraggedOverTopic !==
                            topic.id}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="h-6 w-6"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M9 13.5l3 3m0 0l3-3m-3 3v-6m1.06-4.19l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
                            />
                        </svg>
                    </div>
                </a>

                {#if canRemoveTopicsFromCluster}
                    <div class="sidebar-list__item-drag-image">
                        <svg
                            class=""
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M3.75 9h16.5m-16.5 6.75h16.5"
                            />
                        </svg>
                    </div>
                {/if}
            </div>
        {/each}
    {/if}
</div>

<style>
    .sidebar-list-title-content {
        @apply z-10 flex min-h-8 select-none flex-row items-center rounded p-1 pl-2 text-sm font-bold shadow-lg shadow-gray-100 hover:bg-gray-200;
    }

    .sidebar-list-title__name {
        @apply ml-2 flex-1 cursor-pointer rounded outline-none;
    }

    .sidebar-list-title__button {
        @apply ml-auto grid h-6 w-6 cursor-pointer place-items-center rounded opacity-100 hover:bg-gray-300 group-hover:opacity-100 sm:opacity-0;
    }

    .sidebar-list-title__button svg {
        @apply h-4 w-4;
    }

    .dragged-topic-over-cluster {
        @apply bg-gray-200 !important;
    }

    .sidebar-list {
        @apply max-h-fit overflow-hidden transition-all duration-300 ease-in-out;
    }

    .sidebar-list--hidden {
        @apply max-h-0;
    }

    .sidebar-list__item-wrapper {
        @apply relative flex;
    }

    .sidebar-list__item-drop-area {
        @apply absolute inset-0 z-10;
    }

    .sidebar-list__item-drop-area--hidden {
        @apply hidden;
    }

    .sidebar-list__item {
        @apply flex flex-1 cursor-pointer select-none flex-row items-start rounded py-1 pl-7 pr-4 text-sm tracking-tight hover:bg-gray-200;
    }

    .sidebar-list__item--loading {
        @apply text-gray-400 hover:bg-transparent;
    }

    .sidebar-list__item--dragover {
        @apply pl-2 pr-10;
    }

    .sidebar-list__item-name {
        @apply flex-1 break-words;
    }

    .sidebar-list__item-name--active {
        @apply font-bold;
    }

    .sidebar-list__item-icon {
        @apply ml-0.5 mr-2 mt-0.5 h-4 w-4 min-w-4;
    }

    .sidebar-list__item-drop-image {
        @apply absolute right-0 flex h-full w-10 items-center justify-center overflow-hidden bg-gray-200 text-gray-400 transition-all;
    }

    .sidebar-list__item-drop-image--hidden {
        @apply w-0;
    }

    .sidebar-list__item-drop-image svg {
        @apply h-4 w-4;
    }

    .sidebar-list__item-drag-image {
        @apply flex h-full items-center p-2 sm:hidden;
    }

    .sidebar-list__item-drag-image svg {
        @apply h-4 w-4 min-w-4 text-gray-300;
    }

    .menu-hidden {
        @apply invisible h-0 opacity-0 !important;
    }

    /* purgecss start ignore */
    :global(.sortable-clusters-wrapper .sortable-ghost) .sidebar-list-title {
        @apply hidden;
    }

    :global(.sortable-clusters-wrapper .sortable-ghost) .sidebar-list {
        @apply h-0;
    }

    :global(.sidebar-list__item-wrapper.sortable-ghost) {
        @apply block h-1 touch-none overflow-hidden !important;
    }

    :global(.sidebar-list__item-wrapper.sortable-ghost)::before {
        @apply absolute left-0 right-0 z-max h-0.5 w-full bg-gray-700 content-[''];
    }

    /* purgecss end ignore */
</style>
